import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AppModal from "../../components/AppModal";

const API_INMUEBLES = `${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/`;
const API_CIUDADES = `${process.env.REACT_APP_API_URL || ""}/api/ciudades/`;

const initialSearch = {
  TBNoMatricula: "",
  TBDireccion: "",
};

const initialForm = {
  TBNoMatricula: "",
  TBDireccion: "",
  TBDireccion_id: null,
  TCId: "11001",
  TBTipo: "RESIDENCIAL",
  TBObs: "",
  estrato: "3",
  pin_verificacion: "",
  fecha_hora_impresion: "",
  situacion_propiedad: "",
  owner_persona: "",
  numero_contrato_energia: "",
  numero_contrato_agua: "",
  numero_contrato_gas: "",
};

export default function PasoInmueble({ onSuccess, onBack }) {
  const [form, setForm] = useState(initialForm);
  const [searchForm, setSearchForm] = useState(initialSearch);
  const [ciudades, setCiudades] = useState([]);
  const [arrendadores, setArrendadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [inmuebleSeleccionado, setInmuebleSeleccionado] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });

  const [certificadoFile, setCertificadoFile] = useState(null);
  const [snrStatus, setSnrStatus] = useState(null);
  const [verificandoSNR, setVerificandoSNR] = useState(false);
  const [direccionesSugeridas, setDireccionesSugeridas] = useState([]);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");

  const showModal = (title, message, type = "info") => {
    setModal({ isOpen: true, title, message, type });
  };

  const formatApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    if (!data) return fallbackMessage;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;

    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return `${firstKey}: ${firstValue[0]}`;
    }

    if (typeof firstValue === "string") {
      return `${firstKey}: ${firstValue}`;
    }

    return fallbackMessage;
  };

  const getCiudadLabel = (ciudad) => {
    const parts = [ciudad?.TCNombre?.trim(), ciudad?.TCDepartamento?.trim(), ciudad?.TCPais?.trim()].filter(Boolean);
    return parts.length > 0 ? parts.join(" - ") : `Ciudad ${ciudad?.TCId ?? ""}`;
  };

  const getCiudadNombre = (tcId) => {
    const ciudad = ciudades.find((c) => String(c.TCId) === String(tcId));
    return ciudad ? getCiudadLabel(ciudad) : `Ciudad ${tcId || ""}`;
  };

  const resetSearchFlow = () => {
    setSearchResults([]);
    setSearchMessage("");
    setShowCreateForm(false);
    setForm(initialForm);
    setCertificadoFile(null);
    setSnrStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!token) {
      setError("No estás autenticado. Inicia sesión primero.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    Promise.all([
      axios.get(API_INMUEBLES, { headers: { Authorization: `Token ${token}` } }),
      axios.get(API_CIUDADES, { headers: { Authorization: `Token ${token}` } }),
      axios.get(`${process.env.REACT_APP_API_URL || ""}/api/persona/`, { headers: { Authorization: `Token ${token}` } }).catch(() => ({ data: [] })),
    ])
      .then(([inmueblesRes, ciudadesRes, personasRes]) => {
        setCiudades(ciudadesRes.data || []);
        setArrendadores(personasRes.data || []);
        if (inmueblesRes.data && Array.isArray(inmueblesRes.data) && inmueblesRes.data.length > 0) {
          // Pre-populate search results if available
          setSearchResults(inmueblesRes.data);
        }
      })
      .catch((err) => {
        setError(formatApiError(err, "No fue posible cargar los datos iniciales"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchForm((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "TCId" || name === "owner_persona" || name === "estrato" ? value : value.toUpperCase(),
    }));
  };

  const handleCertificadoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCertificadoFile(e.target.files[0]);
    } else {
      setCertificadoFile(null);
    }
  };

  const handleMatriculaBlur = (e) => {
    const matricula = e.target.value;
    if (matricula && matricula.trim()) {
      axios
        .get(`${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/buscar_por_matricula/?matricula=${encodeURIComponent(matricula.trim())}`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => setDireccionesSugeridas(res.data || []))
        .catch((err) => console.error(err));
    } else {
      setDireccionesSugeridas([]);
    }
  };

  const handleVerificarPinSNR = async () => {
    if (!form.pin_verificacion || !form.pin_verificacion.trim()) {
      return showModal(
        "PIN Requerido",
        "Por favor ingresa el Número de PIN del Certificado de Tradición para verificar ante la Supernotariado.",
        "error"
      );
    }

    setVerificandoSNR(true);
    setSnrStatus({
      loading: true,
      verificado: false,
      mensaje: "Consultando portal de la Supernotariado (validate.snr) y comprobando Matrícula, Fecha y PIN...",
      estado: "CARGANDO",
    });

    try {
      const fd = new FormData();
      fd.append("pin", form.pin_verificacion.trim());
      if (form.TBNoMatricula) {
        fd.append("matricula", form.TBNoMatricula.trim());
      }
      if (form.fecha_hora_impresion) {
        fd.append("fecha_finalizacion_generacion", form.fecha_hora_impresion.trim());
      }
      if (certificadoFile) {
        fd.append("certificado_tradicion", certificadoFile);
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/api/bienesinmuebles/verificar-pin-snr/`,
        fd,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "multipart/form-data"
          },
        }
      );

      setSnrStatus({
        loading: false,
        verificado: res.data.valido,
        mensaje: res.data.mensaje,
        estado: res.data.estado,
        detalle: res.data.detalle,
        comparacion: res.data.comparacion,
        fecha_verificacion: res.data.fecha_verificacion,
        matricula_snr: res.data.matricula_snr,
        fecha_snr: res.data.fecha_snr,
      });

      // Auto-completar matrícula o fecha si la SNR o el PDF las retornaron y no estaban diligenciadas
      if (res.data.fecha_snr && !form.fecha_hora_impresion) {
        setForm(prev => ({ ...prev, fecha_hora_impresion: res.data.fecha_snr }));
      }
      if (res.data.matricula_snr && !form.TBNoMatricula) {
        setForm(prev => ({ ...prev, TBNoMatricula: res.data.matricula_snr }));
      }

      if (res.data.valido) {
        showModal("Verificación SNR Exitosa", res.data.mensaje, "success");
      } else {
        showModal("Resultado de Validación SNR", res.data.mensaje, "error");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.mensaje || err.response?.data?.detail || "Error al conectar con la Superintendencia de Notariado y Registro.";
      setSnrStatus({
        loading: false,
        verificado: false,
        mensaje: errorMsg,
        estado: "ERROR",
      });
      showModal("Error SNR", errorMsg, "error");
    } finally {
      setVerificandoSNR(false);
    }
  };

  const normalizeDireccion = async (direccion, ciudadId) => {
    const direccionOriginal = String(direccion || "").trim();
    if (!direccionOriginal) {
      return { direccion: "", direccionId: null };
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/api/direcciones/normalizar/`,
        { direccion: direccionOriginal, ciudad_id: ciudadId ? parseInt(ciudadId, 10) : null },
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.datos) {
        return {
          direccion: response.data.datos.direccion_normalizada,
          direccionId: response.data.datos.id,
        };
      }
    } catch (err) {
      console.warn("Error normalizando dirección", err);
    }

    return { direccion: direccionOriginal, direccionId: null };
  };

  const buscarInmueble = async () => {
    const rawMatricula = String(searchForm.TBNoMatricula ?? "").trim();
    const rawDireccion = String(searchForm.TBDireccion ?? "").trim();

    const actualMatricula = rawMatricula || document.querySelector('input[name="TBNoMatricula"]')?.value || "";
    const actualDireccion = rawDireccion || document.querySelector('input[name="TBDireccion"]')?.value || "";

    const matricula = actualMatricula.trim().toLowerCase();
    const direccion = actualDireccion.trim().toLowerCase();

    if (!matricula && !direccion) {
      showModal("Atención", "Debes ingresar la Matrícula, la Dirección o ambas para buscar", "error");
      return;
    }

    if (!rawMatricula && actualMatricula) {
      setSearchForm((prev) => ({ ...prev, TBNoMatricula: actualMatricula.toUpperCase() }));
    }
    if (!rawDireccion && actualDireccion) {
      setSearchForm((prev) => ({ ...prev, TBDireccion: actualDireccion.toUpperCase() }));
    }

    setSearching(true);
    setInmuebleSeleccionado(null);
    setSearchResults([]);
    setSearchMessage("");
    setShowCreateForm(false);

    try {
      const queryParams = new URLSearchParams();
      if (matricula) queryParams.append("matricula", matricula);
      if (direccion) queryParams.append("direccion", direccion);

      const res = await axios.get(`${API_INMUEBLES}buscar/?${queryParams.toString()}`, {
        headers: { Authorization: `Token ${token}` },
      });
      const results = res.data;

      setSearchResults(results);

      if (results.length > 0) {
        setSearchMessage(`Se encontraron ${results.length} inmueble(s). Selecciona el que hará parte del contrato.`);

        const currentUser = localStorage.getItem("user");
        const isAdmin = localStorage.getItem("role") === "ADMINISTRADOR";

        const hasOtherOwner = results.some((item) => item.username && item.username !== currentUser && !isAdmin);
        const hasActiveContract = results.some((item) => item.estado_arrendado);

        if (hasOtherOwner) {
          showModal("Aviso", "Este Bien Inmueble se encuentra registrado por otro Arrendador", "warning");
        } else if (hasActiveContract) {
          showModal("Aviso", "Este Bien Inmueble tiene un Contrato Vigente", "warning");
        }
      } else {
        setShowCreateForm(true);
        setForm((prev) => ({
          ...initialForm,
          TBNoMatricula: actualMatricula.trim().toUpperCase(),
          TBDireccion: actualDireccion.trim().toUpperCase(),
        }));
        setSearchMessage("No existe un inmueble con esos datos. Completa el formulario y aporta el Certificado de Tradición para crearlo.");
      }
    } catch (err) {
      console.error(err);
      showModal("Error", "Ocurrió un error al buscar los inmuebles.", "error");
    } finally {
      setSearching(false);
    }
  };

  const guardarInmueble = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!form.TBNoMatricula || !form.TBNoMatricula.trim()) {
      showModal("Atención", "El Número de Matrícula es obligatorio.", "error");
      return;
    }

    if (!form.TBDireccion || !form.TBDireccion.trim()) {
      showModal("Atención", "La Dirección es obligatoria.", "error");
      return;
    }

    if (!form.TCId) {
      showModal("Atención", "Debe seleccionar una Ciudad.", "error");
      return;
    }

    if (!form.TBTipo) {
      showModal("Atención", "Debe seleccionar el Tipo de Inmueble.", "error");
      return;
    }

    if (!certificadoFile) {
      showModal(
        "Certificado de Tradición Obligatorio",
        "Para la creación de un Bien Inmueble es obligatorio aportar el Certificado de Tradición (archivo PDF).",
        "error"
      );
      return;
    }

    if (!form.pin_verificacion || form.pin_verificacion.trim().length === 0) {
      showModal(
        "Número de PIN Requerido",
        "Debe suministrar el Número de PIN de verificación del Certificado de Tradición.",
        "error"
      );
      return;
    }

    if (!form.fecha_hora_impresion || form.fecha_hora_impresion.trim().length === 0) {
      showModal(
        "Fecha Finalización Generación Requerida",
        "Debe indicar la Fecha Finalización Generación en formato 03/04/2025 03:20 PM que figura en el Certificado de Tradición.",
        "error"
      );
      return;
    }

    if (!form.situacion_propiedad || form.situacion_propiedad.trim().length === 0) {
      showModal(
        "Situación de Propiedad Requerida",
        "Debe registrar la situación actual de propiedad que consta en el Certificado de Tradición (titulares, gravámenes, etc.).",
        "error"
      );
      return;
    }

    setSubmitting(true);
    try {
      const normalized = await normalizeDireccion(form.TBDireccion, form.TCId);
      const formData = new FormData();
      formData.append("TBNoMatricula", form.TBNoMatricula.trim());
      formData.append("TBDireccion", normalized.direccion);
      if (normalized.direccionId) {
        formData.append("TBDireccion_id", normalized.direccionId);
      }
      formData.append("TCId", form.TCId);
      formData.append("TBTipo", form.TBTipo);
      formData.append("estrato", form.estrato);
      if (form.TBObs) formData.append("TBObs", form.TBObs.trim());
      if (form.numero_contrato_energia) formData.append("numero_contrato_energia", form.numero_contrato_energia.trim());
      if (form.numero_contrato_agua) formData.append("numero_contrato_agua", form.numero_contrato_agua.trim());
      if (form.numero_contrato_gas) formData.append("numero_contrato_gas", form.numero_contrato_gas.trim());
      if (form.owner_persona) formData.append("owner_persona", form.owner_persona);

      formData.append("pin_verificacion", form.pin_verificacion.trim());
      formData.append("fecha_finalizacion_generacion", form.fecha_hora_impresion.trim());
      formData.append("fecha_hora_impresion", form.fecha_hora_impresion.trim());
      formData.append("situacion_propiedad", form.situacion_propiedad.trim());
      if (certificadoFile) {
        formData.append("certificado_tradicion", certificadoFile);
      }

      const res = await axios.post(API_INMUEBLES, formData, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const nuevoInmueble = res.data;
      setInmuebleSeleccionado(nuevoInmueble);
      setSearchResults([nuevoInmueble]);
      setShowCreateForm(false);
      setSearchMessage("Inmueble creado y seleccionado para el contrato.");
      setSearchForm({
        TBNoMatricula: nuevoInmueble.TBNoMatricula,
        TBDireccion: nuevoInmueble.TBDireccion,
      });
      setForm(initialForm);
      setCertificadoFile(null);
      setSnrStatus(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      showModal("Inmueble Creado", "Bien Inmueble registrado y verificado exitosamente ante Supernotariado. Ha quedado seleccionado para el contrato.", "success");
    } catch (err) {
      showModal("Error al guardar inmueble", formatApiError(err, "Verifica los datos ingresados."), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const continuar = () => {
    if (!inmuebleSeleccionado?.id) {
      showModal("Atención", "Debes seleccionar un inmueble válido para continuar.", "error");
      return;
    }
    onSuccess(inmuebleSeleccionado.id);
  };

  return (
    <div className="wizard-step-content">
      <section className="app-surface">
        <div className="app-section-title">
          <h2>Paso 2. Bien inmueble</h2>
          <p>Busca un inmueble existente o crea uno nuevo como objeto del contrato.</p>
        </div>

        <div className="wizard-form-grid">
          <label htmlFor="search-TBNoMatricula">Número de Matrícula</label>
          <input
            id="search-TBNoMatricula"
            autoComplete="off"
            name="TBNoMatricula"
            placeholder="Número de Matrícula"
            value={searchForm.TBNoMatricula}
            onChange={handleSearchChange}
          />
          <label htmlFor="search-TBDireccion">Dirección</label>
          <input
            id="search-TBDireccion"
            autoComplete="off"
            name="TBDireccion"
            placeholder="Dirección"
            value={searchForm.TBDireccion}
            onChange={handleSearchChange}
            list="direcciones-list"
            pattern="^[a-zA-Z]+\s[0-9a-zA-Z]+\s[0-9a-zA-Z]+\s\d+(?:\s.+)?$"
            title="Debe usar el formato: Vía Número Letra Número Letra Número Complemento. Ej: C 60 18 15 o K 21B 35 28 Ap 201"
          />
          <div style={{ display: "flex", gap: "8px", gridColumn: "1 / -1" }}>
            <button className="app-button app-button--primary" type="button" onClick={buscarInmueble} disabled={searching}>
              {searching ? "Buscando..." : "Buscar Inmueble"}
            </button>
            <button
              className="app-button app-button--secondary"
              type="button"
              onClick={() => {
                setShowCreateForm(true);
                setSearchMessage("Ingresa los datos y aporta el Certificado de Tradición para el nuevo bien raíz.");
                setForm((prev) => ({
                  ...initialForm,
                  TBNoMatricula: (searchForm.TBNoMatricula || "").trim().toUpperCase(),
                  TBDireccion: (searchForm.TBDireccion || "").trim().toUpperCase(),
                }));
              }}
            >
              + Crear Nuevo Inmueble
            </button>
          </div>
        </div>

        <datalist id="direcciones-list">
          {direccionesSugeridas.map((dir, idx) => (
            <option key={idx} value={dir} />
          ))}
        </datalist>

        {loading && <div className="app-message app-message--info" style={{ marginTop: "12px" }}>Cargando datos...</div>}
        {error && <div className="app-message app-message--error" style={{ marginTop: "12px" }}>{error}</div>}
        {searchMessage && <div className="app-message app-message--success" style={{ marginTop: "12px" }}>{searchMessage}</div>}

        {searchResults.length > 0 && (
          <div className="wizard-choice-list" style={{ marginTop: "16px" }}>
            {searchResults.map((item) => (
              <div
                key={item.id}
                className={`wizard-choice ${inmuebleSeleccionado?.id === item.id ? "wizard-choice--selected" : ""}`}
              >
                <div>
                  <strong>Matrícula: {item.TBNoMatricula}</strong>
                  <small style={{ display: "block" }}>Dirección: {item.TBDireccion}</small>
                  <small style={{ display: "block" }}>
                    Tipo: {item.TBTipo} - Estrato: {item.estrato || "N/A"} - Ciudad: {getCiudadNombre(item.TCId)}
                  </small>
                  {item.snr_verificado && (
                    <small style={{ color: "#059669", fontWeight: "bold", display: "block" }}>
                      ✓ Verificado ante Supernotariado (PIN: {item.pin_verificacion || "OK"})
                    </small>
                  )}
                </div>
                <button
                  className="app-button app-button--primary"
                  type="button"
                  onClick={() => setInmuebleSeleccionado(item)}
                  disabled={
                    item.estado_arrendado ||
                    (item.username && item.username !== localStorage.getItem("user") && localStorage.getItem("role") !== "ADMINISTRADOR")
                  }
                >
                  {inmuebleSeleccionado?.id === item.id ? "✓ Seleccionado" : "Seleccionar"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FORMULARIO DE CREACIÓN DE NUEVO BIEN INMUEBLE CON CERTIFICADO Y VALIDACIÓN SNR */}
      {showCreateForm && (
        <section className="app-surface">
          <div className="app-section-title">
            <h2>Registrar Nuevo Bien Inmueble</h2>
            <p>
              Completa los datos del inmueble y adjunta el Certificado de Tradición con su PIN para la verificación oficial ante la Supernotariado.
            </p>
          </div>

          <form onSubmit={guardarInmueble} className="app-form-grid" autoComplete="off">
            <div className="app-field">
              <label htmlFor="form-TBNoMatricula">
                No. Matrícula <span style={{ color: "#e11d48" }}>*</span>
              </label>
              <input
                id="form-TBNoMatricula"
                type="text"
                name="TBNoMatricula"
                value={form.TBNoMatricula}
                onChange={handleFormChange}
                onBlur={handleMatriculaBlur}
                required
                placeholder="Ej: 040-12345"
                className="app-input"
              />
            </div>

            <div className="app-field">
              <label htmlFor="form-TBDireccion">
                Dirección <span style={{ color: "#e11d48" }}>*</span>
              </label>
              <input
                id="form-TBDireccion"
                type="text"
                name="TBDireccion"
                value={form.TBDireccion}
                onChange={handleFormChange}
                required
                list="direcciones-list"
                pattern="^[a-zA-Z]+\s[0-9a-zA-Z]+\s[0-9a-zA-Z]+\s\d+(?:\s.+)?$"
                title="Debe usar el formato: Vía Número Letra Número Letra Número Complemento. Ej: C 60 18 15 o K 21B 35 28 Ap 201"
                placeholder="Ej: K 21B 35 28 Ap 201"
                className="app-input"
              />
              <small style={{ color: "#666", fontSize: "11px" }}>
                Formato: Vía Principal + Número + Letra + Número + Letra + Número [+ Complemento]. Ej: C 60 18 15
              </small>
            </div>

            <div className="app-field">
              <label htmlFor="form-TCId">
                Ciudad <span style={{ color: "#e11d48" }}>*</span>
              </label>
              <select id="form-TCId" name="TCId" value={form.TCId} onChange={handleFormChange} required className="app-select">
                <option value="">-- Seleccionar Ciudad --</option>
                {ciudades.map((ciudad) => (
                  <option key={ciudad.TCId} value={ciudad.TCId}>
                    {ciudad.TCNombre}
                    {ciudad.TCDepartamento ? ` - ${ciudad.TCDepartamento}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="app-field">
              <label htmlFor="form-TBTipo">
                Tipo de Inmueble <span style={{ color: "#e11d48" }}>*</span>
              </label>
              <select id="form-TBTipo" name="TBTipo" value={form.TBTipo} onChange={handleFormChange} required className="app-select">
                <option value="RESIDENCIAL">RESIDENCIAL</option>
                <option value="COMERCIAL">COMERCIAL</option>
                <option value="INDUSTRIAL">INDUSTRIAL</option>
              </select>
            </div>

            <div className="app-field">
              <label htmlFor="form-estrato">
                Estrato <span style={{ color: "#e11d48" }}>*</span>
              </label>
              <select id="form-estrato" name="estrato" value={form.estrato} onChange={handleFormChange} required className="app-select">
                <option value="1">1 - Bajo-bajo</option>
                <option value="2">2 - Bajo</option>
                <option value="3">3 - Medio-bajo</option>
                <option value="4">4 - Medio</option>
                <option value="5">5 - Medio-alto</option>
                <option value="6">6 - Alto</option>
              </select>
            </div>

            <div className="app-field">
              <label htmlFor="form-owner_persona">Propietario (Arrendador)</label>
              <select id="form-owner_persona" name="owner_persona" value={form.owner_persona} onChange={handleFormChange} className="app-select">
                <option value="">-- Autocargar mi usuario o seleccionar --</option>
                {arrendadores.map((arr) => (
                  <option key={arr.TPNoDocumento} value={arr.TPNoDocumento}>
                    {arr.TPNombres} {arr.TPApellidos} ({arr.TPNoDocumento})
                  </option>
                ))}
              </select>
            </div>

            {/* SECCIÓN CERTIFICADO DE TRADICIÓN Y VERIFICACIÓN SNR */}
            <div
              style={{
                gridColumn: "1 / -1",
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #bae6fd",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", flexWrap: "wrap", gap: "8px" }}>
                <strong style={{ fontSize: "15px", color: "#0369a1", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>🏛️</span> Certificado de Tradición y Verificación Supernotariado (SNR)
                </strong>
                <a
                  href="https://certificados.supernotariado.gov.co/certificado/external/validation/validate.snr"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "12px", color: "#0284c7", textDecoration: "underline" }}
                >
                  Portal SNR External Validation ↗
                </a>
              </div>
              <p style={{ fontSize: "12px", color: "#0f172a", margin: "0 0 14px 0" }}>
                Para registrar el Bien Inmueble debe aportar el Certificado de Tradición y los datos correspondientes. El sistema verifica el Nro. de PIN en la web oficial de la Superintendencia de Notariado y Registro.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px" }}>
                <div className="app-field">
                  <label>
                    Certificado de Tradición (PDF) <span style={{ color: "#e11d48" }}>*</span>
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="application/pdf"
                    onChange={handleCertificadoChange}
                    className="app-input"
                    required
                  />
                  <small style={{ color: "#64748b", fontSize: "11px" }}>Archivo PDF legible expedido por la SNR.</small>
                </div>

                <div className="app-field">
                  <label>
                    Nro. de PIN de Verificación <span style={{ color: "#e11d48" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      name="pin_verificacion"
                      value={form.pin_verificacion}
                      onChange={handleFormChange}
                      placeholder="Ej: 20240101-12345678"
                      className="app-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleVerificarPinSNR}
                      disabled={verificandoSNR || !form.pin_verificacion}
                      className="app-button app-button--secondary"
                      style={{ whiteSpace: "nowrap", fontSize: "12px", padding: "6px 12px" }}
                      title="Verificar PIN en https://certificados.supernotariado.gov.co/certificado/external/validation/validate.snr"
                    >
                      {verificandoSNR ? "Verificando..." : "Validar PIN SNR"}
                    </button>
                  </div>
                  {snrStatus && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        backgroundColor: snrStatus.verificado ? "#ecfdf5" : "#fef2f2",
                        border: `1px solid ${snrStatus.verificado ? "#a7f3d0" : "#fecaca"}`,
                        color: snrStatus.verificado ? "#065f46" : "#991b1b",
                      }}
                    >
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        {snrStatus.verificado ? "✓ Verificación Conforme: " : "⚠️ Alerta de Verificación: "}
                      </strong>
                      <div>{snrStatus.mensaje}</div>
                      {snrStatus.comparacion && (
                        <div style={{ marginTop: "6px", fontSize: "11px", display: "grid", gap: "2px" }}>
                          <div>{snrStatus.comparacion.matricula_coincide ? "✓" : "✗"} a. Matrícula Inmobiliaria comparada</div>
                          <div>{snrStatus.comparacion.fecha_generacion_coincide ? "✓" : "✗"} b. Fecha Finalización Generación (03/04/2025 03:20 PM) comparada</div>
                          <div>{snrStatus.comparacion.pin_adjuntado_coincide ? "✓" : "✗"} c. PIN verificado con Certificado de Tradición adjuntado</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="app-field">
                  <label>
                    Fecha Finalización Generación (dd/mm/aaaa hh:mm PM) <span style={{ color: "#e11d48" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="fecha_hora_impresion"
                    value={form.fecha_hora_impresion}
                    onChange={handleFormChange}
                    placeholder="03/04/2025 03:20 PM"
                    className="app-input"
                    required
                  />
                  <small style={{ color: "#64748b", fontSize: "11px" }}>Formato: 03/04/2025 03:20 PM (como figura en el Certificado de Tradición).</small>
                </div>

                <div className="app-field" style={{ gridColumn: "1 / -1" }}>
                  <label>
                    Situación Actual de Propiedad <span style={{ color: "#e11d48" }}>*</span>
                  </label>
                  <textarea
                    name="situacion_propiedad"
                    value={form.situacion_propiedad}
                    onChange={handleFormChange}
                    rows="2"
                    placeholder="Ej: Propietario actual según anotación registral, libre de gravámenes, sin embargos ni patrimonio inembargable."
                    className="app-textarea"
                    required
                  />
                  <small style={{ color: "#64748b", fontSize: "11px" }}>Indique la situación jurídica actual según consta en el Certificado.</small>
                </div>
              </div>
            </div>

            <div className="app-field">
              <label htmlFor="form-TBObs">Observaciones (Opcional)</label>
              <input
                id="form-TBObs"
                type="text"
                name="TBObs"
                placeholder="Observaciones adicionales"
                value={form.TBObs}
                onChange={handleFormChange}
                className="app-input"
              />
            </div>

            <div className="app-field">
              <label htmlFor="form-energia">No. Contrato Energía (Opcional)</label>
              <input
                id="form-energia"
                type="text"
                name="numero_contrato_energia"
                placeholder="Ej: 12345678"
                value={form.numero_contrato_energia}
                onChange={handleFormChange}
                className="app-input"
              />
            </div>

            <div className="app-field">
              <label htmlFor="form-agua">No. Contrato Agua (Opcional)</label>
              <input
                id="form-agua"
                type="text"
                name="numero_contrato_agua"
                placeholder="Ej: 87654321"
                value={form.numero_contrato_agua}
                onChange={handleFormChange}
                className="app-input"
              />
            </div>

            <div className="app-field">
              <label htmlFor="form-gas">No. Contrato Gas (Opcional)</label>
              <input
                id="form-gas"
                type="text"
                name="numero_contrato_gas"
                placeholder="Ej: 11223344"
                value={form.numero_contrato_gas}
                onChange={handleFormChange}
                className="app-input"
              />
            </div>

            <div className="app-actions" style={{ gridColumn: "1 / -1", marginTop: "12px" }}>
              <button className="app-button app-button--primary" type="submit" disabled={submitting}>
                {submitting ? "Guardando..." : "Guardar y seleccionar inmueble"}
              </button>
              <button className="app-button app-button--secondary" type="button" onClick={resetSearchFlow}>
                Cancelar
              </button>
            </div>
          </form>
        </section>
      )}

      {inmuebleSeleccionado && (
        <section className="app-surface">
          <div className="app-section-title">
            <h2>Bien Inmueble Seleccionado</h2>
          </div>
          <div className="wizard-summary">
            <div>
              <strong>Matrícula:</strong> {inmuebleSeleccionado.TBNoMatricula}
            </div>
            <div>
              <strong>Dirección:</strong> {inmuebleSeleccionado.TBDireccion}
            </div>
            <div>
              <strong>Tipo:</strong> {inmuebleSeleccionado.TBTipo}
            </div>
            <div>
              <strong>Estrato:</strong> {inmuebleSeleccionado.estrato || "N/A"}
            </div>
            <div>
              <strong>Ciudad:</strong> {getCiudadNombre(inmuebleSeleccionado.TCId)}
            </div>
            {inmuebleSeleccionado.pin_verificacion && (
              <div>
                <strong>PIN de Verificación SNR:</strong> {inmuebleSeleccionado.pin_verificacion}{" "}
                {inmuebleSeleccionado.snr_verificado ? "✓ (Verificado)" : ""}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="app-surface">
        <div className="app-actions">
          <button className="app-button app-button--secondary" type="button" onClick={onBack}>
            Regresar a Personas
          </button>
          <button
            className="app-button app-button--primary"
            type="button"
            onClick={continuar}
            disabled={
              !inmuebleSeleccionado?.id ||
              inmuebleSeleccionado?.estado_arrendado ||
              (inmuebleSeleccionado?.username &&
                inmuebleSeleccionado?.username !== localStorage.getItem("user") &&
                localStorage.getItem("role") !== "ADMINISTRADOR")
            }
          >
            Seguir a Contrato
          </button>
        </div>
      </section>

      <AppModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ ...modal, isOpen: false })}
      />
    </div>
  );
}
